import * as React from 'react';
import { Controller, useForm, type FieldPath } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FileSearch, Plus, Save, Trash2 } from 'lucide-react';
import {
  JD_MAX_PAGES,
  JD_RATE_FACT_INDEX,
  type CheckResult,
  type CheckSummary,
  type DocLang,
  type JdContent,
  type JdContentInput,
} from '@clawhire/shared';
import { api, isApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { DateInput } from '@/components/ui/date-input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SkeletonLines } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toaster';
import { Banner } from '../components/Banner';
import { BulletListEditor } from '../components/BulletListEditor';
import { PdfFrame } from '../components/PdfFrame';
import { useBase64ObjectUrl } from '../components/pdf-utils';
import { useInvalidatePositions } from '../queries';
import type { JdGenerateResponse, JdPreviewResponse, PositionDetail } from '../types';
import {
  checksFromErrorDetails,
  fromContent,
  localPrefill,
  normalizeChecks,
  validateContent,
  type ContentIssue,
  type JdFormValues,
} from './jd-form';

export interface JdVersionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  position: PositionDetail;
  isAdmin: boolean;
}

/**
 * "New JD version": prefilled form bound to `JdContent`, "Generate & preview" (PDF + checks) and
 * "Save version" with the CHECKS_FAILED / ON_SITE_BLOCKED admin paths (spec §6.1, §6.4, §7.1).
 */
export function JdVersionDialog({ open, onOpenChange, position, isAdmin }: JdVersionDialogProps) {
  const invalidate = useInvalidatePositions();
  const [lang, setLang] = React.useState<DocLang>('EN');
  const [source, setSource] = React.useState<'api' | 'local' | null>(null);
  const [issues, setIssues] = React.useState<ContentIssue[]>([]);
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [pdfBase64, setPdfBase64] = React.useState<string | null>(null);
  const [pageCount, setPageCount] = React.useState<number | null>(null);
  const [previewChecks, setPreviewChecks] = React.useState<CheckSummary | null>(null);
  const [failedChecks, setFailedChecks] = React.useState<CheckSummary | null>(null);
  const [onSiteMessage, setOnSiteMessage] = React.useState<string | null>(null);
  const [changeNote, setChangeNote] = React.useState('');
  const [forceReason, setForceReason] = React.useState('');
  const [overrideReason, setOverrideReason] = React.useState('');
  const previewUrl = useBase64ObjectUrl(pdfBase64);

  const prefill = useQuery({
    queryKey: ['positions', 'jd-prefill', position.id, lang],
    queryFn: () =>
      api.get<JdContentInput>(`/positions/${encodeURIComponent(position.id)}/jd/prefill`, {
        query: { lang },
      }),
    enabled: open,
    retry: false,
    staleTime: 0,
  });

  const form = useForm<JdFormValues>({ defaultValues: fromContent(null, lang) });
  const applied = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      applied.current = null;
      return;
    }
    let key: string | null = null;
    let values: JdFormValues | null = null;
    if (prefill.data) {
      key = `api:${lang}:${prefill.dataUpdatedAt}`;
      values = fromContent(prefill.data, lang);
    } else if (prefill.error) {
      key = `local:${lang}:${prefill.errorUpdatedAt}`;
      values = fromContent(localPrefill(position, lang), lang);
    }
    if (!key || !values || applied.current === key) return;
    applied.current = key;
    form.reset(values);
    setSource(prefill.data ? 'api' : 'local');
    setIssues([]);
    setServerError(null);
    setPdfBase64(null);
    setPageCount(null);
    setPreviewChecks(null);
    setFailedChecks(null);
    setOnSiteMessage(null);
  }, [
    open,
    lang,
    prefill.data,
    prefill.error,
    prefill.dataUpdatedAt,
    prefill.errorUpdatedAt,
    form,
    position,
  ]);

  React.useEffect(() => {
    if (!open) {
      setChangeNote('');
      setForceReason('');
      setOverrideReason('');
    }
  }, [open]);

  /** Zod-validate with `jdContentSchema`; maps issues onto the fields and the summary box. */
  const validate = (): JdContent | null => {
    const result = validateContent(form.getValues());
    form.clearErrors();
    if (result.content) {
      setIssues([]);
      return result.content;
    }
    for (const issue of result.issues) {
      if (issue.path)
        form.setError(issue.path as FieldPath<JdFormValues>, { type: 'schema', message: issue.message });
    }
    setIssues(result.issues);
    return null;
  };

  const preview = useMutation({
    mutationFn: (content: JdContent) =>
      api.post<JdPreviewResponse>(`/positions/${encodeURIComponent(position.id)}/jd/preview`, {
        lang,
        content,
      }),
    onSuccess: (res) => {
      setServerError(null);
      setPdfBase64(res.pdfBase64);
      setPageCount(res.pageCount ?? null);
      setPreviewChecks(normalizeChecks(res.checks));
    },
    onError: (e: Error) => setServerError(e.message),
  });

  const generate = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<JdGenerateResponse>(`/positions/${encodeURIComponent(position.id)}/jd/generate`, body),
    onSuccess: async (res) => {
      const v = res.version;
      toast.success(
        v ? `JD ${v.reference} v${String(v.version).padStart(2, '0')} saved` : 'JD version saved',
      );
      await invalidate();
      onOpenChange(false);
    },
    onError: (e: Error) => {
      if (isApiError(e)) {
        if (e.code === 'CHECKS_FAILED') {
          setFailedChecks(checksFromErrorDetails(e.details));
          setServerError(null);
          return;
        }
        if (e.code === 'ON_SITE_BLOCKED' || e.status === 403) {
          setOnSiteMessage(e.message);
          setServerError(null);
          return;
        }
        if (e.status === 400) {
          const fieldErrors = e.fieldErrors();
          const list = Object.entries(fieldErrors).map(([path, message]) => ({
            path: path.replace(/^content\./, ''),
            message,
          }));
          if (list.length) {
            setIssues(list);
            return;
          }
        }
      }
      setServerError(e.message);
    },
  });

  const onPreview = () => {
    const content = validate();
    if (content) preview.mutate(content);
  };
  const onSave = () => {
    const content = validate();
    if (!content) return;
    const body: Record<string, unknown> = { position: position.id, lang, content };
    if (changeNote.trim()) body.changeNote = changeNote.trim();
    if (forceReason.trim()) {
      body.force = true;
      body.forceReason = forceReason.trim();
    }
    if (overrideReason.trim()) body.overrideOnSiteReason = overrideReason.trim();
    generate.mutate(body);
  };

  const facts = form.watch('facts');
  const stack = form.watch('stack');
  const errorOf = (name: FieldPath<JdFormValues>) => form.getFieldState(name, form.formState).error?.message;
  const loading = open && prefill.isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[96vh] w-[calc(100%-1rem)] max-w-[90rem] flex-col gap-0 overflow-hidden p-0 sm:h-[94vh]">
        <DialogHeader className="border-b px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3 pr-8">
            <div>
              <DialogTitle>New JD version</DialogTitle>
              <DialogDescription>
                {position.requestNo} · {position.role}
                {position.jdReference ? ` · ${position.jdReference}` : ''}
                {source === 'local' ? ' · draft built from the position (prefill unavailable)' : ''}
              </DialogDescription>
            </div>
            <div role="group" aria-label="Language" className="inline-flex rounded-md border p-0.5">
              {(['EN', 'DE'] as DocLang[]).map((code) => (
                <Button
                  key={code}
                  type="button"
                  size="sm"
                  variant={lang === code ? 'secondary' : 'ghost'}
                  aria-pressed={lang === code}
                  className="h-7"
                  onClick={() => setLang(code)}
                >
                  {code}
                </Button>
              ))}
            </div>
          </div>
        </DialogHeader>

        <div className="grid min-h-0 flex-1 lg:grid-cols-2">
          <form
            className="scrollbar-thin min-h-0 space-y-5 overflow-y-auto px-6 py-4 lg:border-r"
            onSubmit={(e) => {
              e.preventDefault();
              onPreview();
            }}
            aria-busy={loading}
          >
            {loading ? (
              <SkeletonLines lines={6} />
            ) : (
              <>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label="Reference" htmlFor="jd-ref">
                    <Input id="jd-ref" readOnly className="font-mono" {...form.register('ref')} />
                  </Field>
                  <Field label="Date" htmlFor="jd-date" error={errorOf('date')}>
                    <Controller
                      control={form.control}
                      name="date"
                      render={({ field }) => (
                        <DateInput
                          id="jd-date"
                          value={field.value}
                          onChange={(v) => field.onChange(v ?? '')}
                          className="w-full"
                        />
                      )}
                    />
                  </Field>
                  <Field label="Status" htmlFor="jd-status" error={errorOf('status')}>
                    <Input id="jd-status" placeholder="Open" {...form.register('status')} />
                  </Field>
                </div>
                <Field label="Title" htmlFor="jd-title" error={errorOf('title')}>
                  <Input id="jd-title" {...form.register('title')} />
                </Field>
                <Field label="Subtitle" htmlFor="jd-subtitle" error={errorOf('subtitle')}>
                  <Input id="jd-subtitle" {...form.register('subtitle')} />
                </Field>
                <Field label="Intro" htmlFor="jd-intro" error={errorOf('intro')}>
                  <Textarea id="jd-intro" rows={3} {...form.register('intro')} />
                </Field>

                <fieldset className="space-y-2">
                  <legend className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Facts
                  </legend>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {facts.map((fact, i) => {
                      const isRate = i === JD_RATE_FACT_INDEX;
                      const id = `jd-fact-${i}`;
                      return (
                        <Field key={id} label={fact.label} htmlFor={id} error={errorOf(`facts.${i}.value`)}>
                          <Input
                            id={id}
                            readOnly={isRate}
                            aria-readonly={isRate}
                            title={
                              isRate ? 'Computed from the client rate and the effective margin' : undefined
                            }
                            className={cn(isRate && 'bg-muted text-muted-foreground')}
                            {...form.register(`facts.${i}.value`)}
                          />
                          {isRate ? (
                            <p className="text-[0.75rem] text-muted-foreground">
                              Computed from the client rate and the margin.
                            </p>
                          ) : null}
                        </Field>
                      );
                    })}
                  </div>
                </fieldset>

                <Field
                  label="About"
                  htmlFor="jd-about"
                  error={errorOf('about')}
                  hint="One paragraph per blank line."
                >
                  <Textarea id="jd-about" rows={4} {...form.register('about')} />
                </Field>

                <ListField
                  control={form.control}
                  name="responsibilities"
                  label="Responsibilities"
                  max={10}
                  error={errorOf('responsibilities')}
                />
                <ListField
                  control={form.control}
                  name="mustHave"
                  label="Must-have"
                  max={10}
                  error={errorOf('mustHave')}
                />
                <ListField
                  control={form.control}
                  name="niceToHave"
                  label="Nice-to-have"
                  max={8}
                  error={errorOf('niceToHave')}
                />

                <fieldset className="space-y-2">
                  <legend className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Stack
                  </legend>
                  {stack.length === 0 ? <p className="text-sm text-muted-foreground">No rows.</p> : null}
                  <div className="space-y-1.5">
                    {stack.map((_, i) => (
                      <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-1.5">
                        <Input
                          aria-label={`Stack row ${i + 1} name`}
                          placeholder="e.g. Backend"
                          className="h-8"
                          {...form.register(`stack.${i}.name`)}
                        />
                        <Input
                          aria-label={`Stack row ${i + 1} value`}
                          placeholder="e.g. Java, Spring"
                          className="h-8"
                          {...form.register(`stack.${i}.value`)}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Remove stack row ${i + 1}`}
                          onClick={() =>
                            form.setValue(
                              'stack',
                              stack.filter((__, j) => j !== i),
                              { shouldDirty: true },
                            )
                          }
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={stack.length >= 8}
                    onClick={() =>
                      form.setValue('stack', [...stack, { name: '', value: '' }], { shouldDirty: true })
                    }
                  >
                    <Plus aria-hidden="true" /> Add row
                  </Button>
                  {errorOf('stack') ? <p className="text-xs text-destructive">{errorOf('stack')}</p> : null}
                </fieldset>

                <Field
                  label="Team"
                  htmlFor="jd-team"
                  error={errorOf('team')}
                  hint="Must mention the coordination by data-claw.de."
                >
                  <Textarea id="jd-team" rows={2} {...form.register('team')} />
                </Field>
                <ListField
                  control={form.control}
                  name="process"
                  label="Process"
                  max={8}
                  error={errorOf('process')}
                />
                <Field label="Apply text" htmlFor="jd-apply" error={errorOf('applyText')}>
                  <Textarea id="jd-apply" rows={2} {...form.register('applyText')} />
                </Field>
                <Field label="Apply email" htmlFor="jd-apply-email" error={errorOf('applyEmail')}>
                  <Input id="jd-apply-email" type="email" {...form.register('applyEmail')} />
                </Field>
              </>
            )}
          </form>

          <aside className="scrollbar-thin flex min-h-0 flex-col gap-3 overflow-y-auto bg-muted/30 px-6 py-4">
            <h3 className="text-sm font-medium">Preview</h3>
            {previewUrl ? (
              <>
                <PdfFrame url={previewUrl} title="JD preview" className="h-[60vh] min-h-[20rem]" />
                <p className="text-xs text-muted-foreground">
                  {pageCount !== null
                    ? `${pageCount} page${pageCount === 1 ? '' : 's'}`
                    : 'Page count unknown'}
                  {pageCount !== null && pageCount > JD_MAX_PAGES
                    ? ` – a JD must fit on ${JD_MAX_PAGES} pages`
                    : ''}
                </p>
              </>
            ) : (
              <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                Generate a preview to see the PDF and the content checks.
              </p>
            )}
            {previewChecks ? <ChecksList title="Content checks" summary={previewChecks} /> : null}
          </aside>
        </div>

        <div className="space-y-3 border-t px-6 py-4">
          {issues.length > 0 ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
            >
              <p className="font-medium text-destructive">Fix these before generating:</p>
              <ul className="mt-1 list-disc pl-5 text-destructive">
                {issues.map((i, n) => (
                  <li key={n}>
                    {i.path ? <span className="font-mono text-xs">{i.path}</span> : null} {i.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {serverError ? (
            <p role="alert" className="text-sm font-medium text-destructive">
              {serverError}
            </p>
          ) : null}
          {failedChecks ? (
            <div className="space-y-2">
              <ChecksList
                title="Checks failed – the version was not saved"
                summary={failedChecks}
                tone="danger"
              />
              {isAdmin ? (
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-[16rem] flex-1 space-y-1">
                    <Label htmlFor="jd-force-reason">Force with reason (admin)</Label>
                    <Input
                      id="jd-force-reason"
                      value={forceReason}
                      onChange={(e) => setForceReason(e.target.value)}
                      placeholder="Why this version is acceptable despite the errors"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={!forceReason.trim()}
                    loading={generate.isPending}
                    onClick={onSave}
                  >
                    Force save
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Only an admin can force a version with failing checks.
                </p>
              )}
            </div>
          ) : null}
          {onSiteMessage ? (
            <Banner tone="danger" title="JD generation blocked (on-site rule)">
              {onSiteMessage}
              {isAdmin ? (
                <div className="mt-2 flex flex-wrap items-end gap-2">
                  <div className="min-w-[16rem] flex-1 space-y-1">
                    <Label htmlFor="jd-override-reason">Override reason (admin)</Label>
                    <Input
                      id="jd-override-reason"
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder="Why this on-site position is taken on anyway"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={!overrideReason.trim()}
                    loading={generate.isPending}
                    onClick={onSave}
                  >
                    Override and save
                  </Button>
                </div>
              ) : null}
            </Banner>
          ) : null}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-[14rem] flex-1 space-y-1 sm:max-w-md">
              <Label htmlFor="jd-change-note">Change note (optional)</Label>
              <Input
                id="jd-change-note"
                value={changeNote}
                onChange={(e) => setChangeNote(e.target.value)}
                placeholder="e.g. Rate updated, must-haves tightened"
                maxLength={500}
              />
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onPreview}
                loading={preview.isPending}
                disabled={loading}
              >
                <FileSearch aria-hidden="true" /> Generate &amp; preview
              </Button>
              <Button type="button" onClick={onSave} loading={generate.isPending} disabled={loading}>
                <Save aria-hidden="true" /> Save version
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor} className={cn(error && 'text-destructive')}>
        {label}
      </Label>
      {children}
      {hint && !error ? <p className="text-[0.75rem] text-muted-foreground">{hint}</p> : null}
      {error ? (
        <p role="alert" className="text-[0.75rem] font-medium text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function ListField({
  control,
  name,
  label,
  max,
  error,
}: {
  control: ReturnType<typeof useForm<JdFormValues>>['control'];
  name: 'responsibilities' | 'mustHave' | 'niceToHave' | 'process';
  label: string;
  max: number;
  error?: string;
}) {
  return (
    <fieldset className="space-y-1.5">
      <legend
        className={cn(
          'text-xs font-medium uppercase tracking-wide text-muted-foreground',
          error && 'text-destructive',
        )}
      >
        {label} <span className="font-normal normal-case">(max {max})</span>
      </legend>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <BulletListEditor
            mode="live"
            label={label}
            value={field.value}
            onChange={field.onChange}
            max={max}
          />
        )}
      />
      {error ? (
        <p role="alert" className="text-[0.75rem] font-medium text-destructive">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

/** Errors + warnings from the content checks (spec §6.4). */
export function ChecksList({
  title,
  summary,
  tone,
}: {
  title: string;
  summary: CheckSummary;
  tone?: 'danger';
}) {
  const rows: CheckResult[] = [...summary.errors, ...summary.warnings];
  return (
    <section
      aria-label={title}
      className={cn('rounded-md border p-3 text-sm', tone === 'danger' && 'border-destructive/40')}
    >
      <p className="font-medium">
        {title}:{' '}
        {rows.length === 0 ? (
          <span className="text-emerald-700 dark:text-emerald-400">all checks passed</span>
        ) : (
          <span className="text-muted-foreground">
            {summary.errors.length} error{summary.errors.length === 1 ? '' : 's'}, {summary.warnings.length}{' '}
            warning
            {summary.warnings.length === 1 ? '' : 's'}
          </span>
        )}
      </p>
      {rows.length > 0 ? (
        <ul className="mt-1.5 space-y-1">
          {rows.map((r, i) => (
            <li key={`${r.code}-${i}`} className="flex items-start gap-2">
              <span
                className={cn(
                  'mt-0.5 shrink-0 rounded px-1 text-[10px] font-semibold uppercase leading-4',
                  r.level === 'error'
                    ? 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
                )}
              >
                {r.level}
              </span>
              <span>
                {r.message}
                {r.path ? (
                  <span className="ml-1 font-mono text-xs text-muted-foreground">{r.path}</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
